/* ═══════════════════════════════════════════════════════════════════════════
   DIE HAPPY ! — Theme JS
   Cart drawer, AJAX add-to-cart, quantity steppers, tier form toggles
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  // ── Cart Drawer ──────────────────────────────────────────────────────────

  const cartDrawer = document.getElementById('cart-drawer');
  const cartOverlay = document.getElementById('cart-overlay');
  const cartBody = document.getElementById('cart-drawer-body');
  const cartCount = document.getElementById('cart-count');
  const cartSubtotal = document.getElementById('cart-subtotal');

  function openCart() {
    cartDrawer?.classList.add('is-open');
    cartOverlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeCart() {
    cartDrawer?.classList.remove('is-open');
    cartOverlay?.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  document.querySelectorAll('[data-open-cart]').forEach(btn =>
    btn.addEventListener('click', openCart)
  );
  document.querySelectorAll('[data-close-cart]').forEach(btn =>
    btn.addEventListener('click', closeCart)
  );
  cartOverlay?.addEventListener('click', closeCart);

  // ── Fetch & Render Cart ──────────────────────────────────────────────────

  async function fetchCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  async function renderCart() {
    const cart = await fetchCart();

    // Update count badge
    if (cartCount) {
      cartCount.textContent = cart.item_count;
      cartCount.style.display = cart.item_count > 0 ? 'inline-flex' : 'none';
    }

    // Update subtotal
    if (cartSubtotal) {
      cartSubtotal.textContent = formatMoney(cart.total_price);
    }

    // Render items
    if (!cartBody) return;

    if (cart.items.length === 0) {
      cartBody.innerHTML = '<div class="na-cart-drawer__empty">Your cart is empty</div>';
      return;
    }

    cartBody.innerHTML = cart.items.map(item => `
      <div class="na-cart-item" data-line-key="${item.key}">
        <div class="na-cart-item__image">
          ${item.image ? `<img src="${item.image}" alt="${item.title}">` : ''}
        </div>
        <div class="na-cart-item__info">
          <div class="na-cart-item__title">${item.product_title}</div>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<div class="na-cart-item__variant">${item.variant_title}</div>` : ''}
          ${item.properties && Object.keys(item.properties).length > 0
            ? `<div class="na-cart-item__properties">
                ${Object.entries(item.properties)
                  .filter(([k]) => !k.startsWith('_'))
                  .map(([k, v]) => `<div class="na-cart-item__prop">${k}: ${v}</div>`)
                  .join('')}
              </div>` : ''}
          <div class="na-cart-item__bottom">
            <span class="na-cart-item__price">${formatMoney(item.final_line_price)}</span>
            <div class="na-qty na-qty--small">
              <button type="button" class="na-qty__btn" data-cart-qty-change="${item.key}" data-delta="-1">−</button>
              <div class="na-qty__value">${item.quantity}</div>
              <button type="button" class="na-qty__btn" data-cart-qty-change="${item.key}" data-delta="1">+</button>
            </div>
            <button class="na-cart-item__remove" data-remove-key="${item.key}">REMOVE</button>
          </div>
        </div>
      </div>
    `).join('');

    // Bind remove buttons
    cartBody.querySelectorAll('[data-remove-key]').forEach(btn => {
      btn.addEventListener('click', async () => {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: btn.dataset.removeKey, quantity: 0 }),
        });
        renderCart();
      });
    });

    // Bind cart quantity +/- buttons
    cartBody.querySelectorAll('[data-cart-qty-change]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.cartQtyChange;
        const delta = parseInt(btn.dataset.delta, 10);
        const item = cart.items.find(i => i.key === key);
        if (!item) return;
        const newQty = Math.max(0, item.quantity + delta);
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: newQty }),
        });
        renderCart();
      });
    });
  }

  // ── AJAX Add to Cart ─────────────────────────────────────────────────────

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-add-to-cart-form]');
    if (!form) return;
    e.preventDefault();

    const btn = form.querySelector('[type="submit"]');
    const originalText = btn?.textContent;
    if (btn) { btn.disabled = true; btn.textContent = 'ADDING...'; }

    try {
      const formData = new FormData(form);
      const data = {};

      // Extract standard fields
      data.id = formData.get('id');
      data.quantity = parseInt(formData.get('quantity') || '1', 10);

      // Extract line item properties
      const properties = {};
      for (const [key, value] of formData.entries()) {
        if (key.startsWith('properties[')) {
          const propName = key.replace('properties[', '').replace(']', '');
          if (value) properties[propName] = value;
        }
      }
      if (Object.keys(properties).length > 0) data.properties = properties;

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const err = await res.json();
        alert(err.description || 'Could not add to cart');
        return;
      }

      await renderCart();
      openCart();
    } catch (err) {
      console.error('Add to cart error:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = originalText; }
    }
  });

  // ── Merch Size Selector ──────────────────────────────────────────────────

  document.querySelectorAll('[data-variant-selector]').forEach(container => {
    const buttons = container.querySelectorAll('.na-merch-card__variant-btn');
    const card = container.closest('.na-merch-card');
    const hiddenInput = card?.querySelector('[name="id"]');
    const addBtn = card?.querySelector('[data-merch-add-btn]');

    buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        buttons.forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        if (hiddenInput) hiddenInput.value = btn.dataset.variantId;

        // Update Add to Cart button based on variant availability
        if (addBtn) {
          const soldOut = btn.dataset.variantSoldOut === 'true';
          addBtn.disabled = soldOut;
          addBtn.textContent = soldOut ? 'SOLD OUT' : 'ADD TO CART';
          addBtn.type = soldOut ? 'button' : 'submit';
        }
      });
    });
  });

  // ── Quantity Steppers ────────────────────────────────────────────────────

  document.querySelectorAll('.na-qty').forEach(stepper => {
    const minusBtn = stepper.querySelector('[data-qty-minus]');
    const plusBtn = stepper.querySelector('[data-qty-plus]');
    const display = stepper.querySelector('.na-qty__value');
    const input = stepper.closest('form')?.querySelector('[name="quantity"]');

    minusBtn?.addEventListener('click', () => {
      let val = parseInt(display?.textContent || '1', 10);
      if (val > 1) {
        val--;
        if (display) display.textContent = val;
        if (input) input.value = val;
      }
    });

    plusBtn?.addEventListener('click', () => {
      let val = parseInt(display?.textContent || '1', 10);
      const max = parseInt(plusBtn.dataset.max || '99', 10);
      if (val < max) {
        val++;
        if (display) display.textContent = val;
        if (input) input.value = val;
      }
    });
  });

  // ── Lightbox ──────────────────────────────────────────────────────────────

  // Create lightbox element
  const lightbox = document.createElement('div');
  lightbox.className = 'na-lightbox';
  lightbox.innerHTML = `
    <button type="button" class="na-lightbox__close" aria-label="Close">✕</button>
    <img class="na-lightbox__img" src="" alt="">
  `;
  document.body.appendChild(lightbox);

  const lightboxImg = lightbox.querySelector('.na-lightbox__img');
  const lightboxClose = lightbox.querySelector('.na-lightbox__close');

  function openLightbox(src, alt) {
    // Clear old image and show spinner while loading
    lightboxImg.style.display = 'none';
    lightbox.classList.add('is-open', 'is-loading');
    document.body.style.overflow = 'hidden';

    const img = new Image();
    img.onload = () => {
      lightboxImg.src = src;
      lightboxImg.alt = alt || '';
      lightboxImg.style.display = '';
      lightbox.classList.remove('is-loading');
    };
    img.src = src;
  }

  function closeLightbox() {
    lightbox.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  lightboxClose.addEventListener('click', (e) => { e.stopPropagation(); closeLightbox(); });
  lightbox.addEventListener('click', closeLightbox);
  lightboxImg.addEventListener('click', (e) => e.stopPropagation());

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && lightbox.classList.contains('is-open')) closeLightbox();
  });

  document.addEventListener('click', (e) => {
    const img = e.target.closest('[data-lightbox-src]');
    if (!img) return;
    e.preventDefault();
    openLightbox(img.dataset.lightboxSrc, img.dataset.lightboxAlt);
  });

  // ── Init ─────────────────────────────────────────────────────────────────

  renderCart();
})();
